// InternshipForm.js - With File Upload for Student Picture & Certificate Image
// UPDATED: Sends files directly to internship_certificate.php via multipart/form-data
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faQrcode, faEye, faTimes, faUpload, faImage, faUser
} from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode';

const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const CERT_API = `${API_BASE}/internship_certificate.php`;

const InternshipForm = () => {
    const { user, currentShop } = useAuth();

    const [certificates, setCertificates] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [debugInfo, setDebugInfo] = useState('');

    // File upload states
    const [studentPicFile, setStudentPicFile] = useState(null);
    const [studentPicPreview, setStudentPicPreview] = useState('');
    const [certFile, setCertFile] = useState(null);
    const [certFilePreview, setCertFilePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    
    const studentPicRef = useRef(null);
    const certFileRef = useRef(null);

    const [formData, setFormData] = useState({
        university_name: '',
        department: '',
        semester: '',
        internship_from: '',
        internship_to: '',
        mode: 'campus',
        role: '',
        issuance_date: new Date().toISOString().split('T')[0],
        student_name: '',
        father_name: '',
        cnic_number: '',
        student_email: '',
        student_phone: '',
        supervisor_name: '',
        supervisor_designation: '',
        student_picture: '',
        certificate_picture: ''
    });

    // Show toast with debug info
    const showToast = (type, message, debug = '') => {
        setToast({ show: true, type, message });
        setDebugInfo(debug);
        setTimeout(() => setToast({ show: false, type: '', message: '' }), 6000);
    };

    // Handle student picture selection
    const handleStudentPicChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('error', 'Please select an image file (JPG, PNG, etc.)');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('error', 'Image must be less than 5MB');
            return;
        }
        
        setStudentPicFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setStudentPicPreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Handle certificate image selection
    const handleCertFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('error', 'Please select an image file (JPG, PNG, etc.)');
            return;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            showToast('error', 'Image must be less than 5MB');
            return;
        }
        
        setCertFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setCertFilePreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Clear file selections
    const clearFiles = () => {
        setStudentPicFile(null);
        setStudentPicPreview('');
        setCertFile(null);
        setCertFilePreview('');
        if (studentPicRef.current) studentPicRef.current.value = '';
        if (certFileRef.current) certFileRef.current.value = '';
    };

    // Fetch certificates
    const fetchCertificates = useCallback(async () => {
        if (!currentShop) {
            setLoading(false);
            showToast('warning', 'Please select a shop first');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(CERT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'list',
                    shop_id: currentShop.shop_id,
                    limit: 500
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setCertificates(data.data || []);
                setFiltered(data.data || []);
                showToast('success', `Loaded ${data.data?.length || 0} certificates`);
            } else {
                showToast('error', data.message || 'Failed to load', JSON.stringify(data));
            }
        } catch (error) {
            console.error('❌ Error:', error);
            showToast('error', 'Network error: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [currentShop]);

    // Load on mount
    useEffect(() => {
        if (currentShop) {
            fetchCertificates();
        }
    }, [currentShop, fetchCertificates]);

    // Filter
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFiltered(certificates);
            return;
        }
        const term = searchTerm.toLowerCase();
        setFiltered(certificates.filter(c =>
            (c.certificate_number || '').toLowerCase().includes(term) ||
            (c.student_name || '').toLowerCase().includes(term) ||
            (c.university_name || '').toLowerCase().includes(term) ||
            (c.role || '').toLowerCase().includes(term) ||
            (c.cnic_number || '').includes(term)
        ));
    }, [searchTerm, certificates]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            university_name: '',
            department: '',
            semester: '',
            internship_from: '',
            internship_to: '',
            mode: 'campus',
            role: '',
            issuance_date: new Date().toISOString().split('T')[0],
            student_name: '',
            father_name: '',
            cnic_number: '',
            student_email: '',
            student_phone: '',
            supervisor_name: '',
            supervisor_designation: '',
            student_picture: '',
            certificate_picture: ''
        });
        setEditingId(null);
        clearFiles();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentShop || !user) {
            showToast('error', 'No shop or user selected');
            return;
        }

        if (!formData.student_name || !formData.university_name || !formData.role) {
            showToast('error', 'Student name, university, and role are required');
            return;
        }

        setSubmitting(true);
        setUploading(true);
        
        try {
            // Build FormData for multipart upload (supports both files and text fields)
            const formPayload = new FormData();
            formPayload.append('action', editingId ? 'update' : 'add');
            formPayload.append('shop_id', currentShop.shop_id);
            formPayload.append('created_by', user.username);
            
            if (editingId) {
                formPayload.append('id', editingId);
            }
            
            // Append all form fields
            const textFields = [
                'university_name', 'department', 'semester', 'internship_from', 'internship_to',
                'mode', 'role', 'issuance_date', 'student_name', 'father_name', 'cnic_number',
                'student_email', 'student_phone', 'supervisor_name', 'supervisor_designation'
            ];
            
            textFields.forEach(field => {
                if (formData[field]) {
                    formPayload.append(field, formData[field]);
                }
            });
            
            // Append student picture URL if exists and no new file selected
            if (!studentPicFile && formData.student_picture) {
                formPayload.append('student_picture', formData.student_picture);
            }
            
            // Append certificate picture URL if exists and no new file selected
            if (!certFile && formData.certificate_picture) {
                formPayload.append('certificate_picture', formData.certificate_picture);
            }
            
            // Append file uploads if selected
            if (studentPicFile) {
                formPayload.append('student_picture_file', studentPicFile);
                console.log('📎 Attached student picture:', studentPicFile.name, `(${(studentPicFile.size / 1024).toFixed(1)}KB)`);
            }
            
            if (certFile) {
                formPayload.append('certificate_picture_file', certFile);
                console.log('📎 Attached certificate image:', certFile.name, `(${(certFile.size / 1024).toFixed(1)}KB)`);
            }

            console.log('📤 Submitting multipart form with', 
                studentPicFile ? 'student picture + ' : '', 
                certFile ? 'certificate image + ' : '', 
                Object.keys(formData).length, 'fields'
            );

            // Send as multipart/form-data (NO Content-Type header - browser sets it automatically with boundary)
            const response = await fetch(CERT_API, {
                method: 'POST',
                body: formPayload
            });

            const data = await response.json();
            console.log('📥 Response:', data);

            if (data.status === 'success') {
                let msg = editingId ? 'Certificate updated!' : 'Certificate created!';
                if (data.data?.uploads) {
                    const uploads = data.data.uploads;
                    if (uploads.student_picture) msg += ' 📸';
                    if (uploads.certificate_picture) msg += ' 📄';
                }
                showToast('success', msg);
                setShowModal(false);
                resetForm();
                fetchCertificates();
            } else {
                showToast('error', data.message || 'Operation failed', JSON.stringify(data));
            }
        } catch (error) {
            console.error('❌ Submit error:', error);
            showToast('error', 'Error: ' + error.message);
        } finally {
            setSubmitting(false);
            setUploading(false);
        }
    };

    const handleEdit = (cert) => {
        setFormData({
            university_name: cert.university_name || '',
            department: cert.department || '',
            semester: cert.semester || '',
            internship_from: cert.internship_from || '',
            internship_to: cert.internship_to || '',
            mode: cert.mode || 'campus',
            role: cert.role || '',
            issuance_date: cert.issuance_date || new Date().toISOString().split('T')[0],
            student_name: cert.student_name || '',
            father_name: cert.father_name || '',
            cnic_number: cert.cnic_number || '',
            student_email: cert.student_email || '',
            student_phone: cert.student_phone || '',
            supervisor_name: cert.supervisor_name || '',
            supervisor_designation: cert.supervisor_designation || '',
            student_picture: cert.student_picture || '',
            certificate_picture: cert.certificate_picture || ''
        });
        setEditingId(cert.id);
        setStudentPicPreview(cert.student_picture || '');
        setCertFilePreview(cert.certificate_picture || '');
        setStudentPicFile(null);
        setCertFile(null);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this certificate permanently?')) return;
        try {
            const response = await fetch(CERT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id: id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', 'Certificate deleted');
                fetchCertificates();
            } else {
                showToast('error', data.message || 'Delete failed');
            }
        } catch (error) {
            showToast('error', 'Delete error: ' + error.message);
        }
    };

    const generateQRCode = async (cert) => {
        if (!currentShop) {
            showToast('error', 'No shop selected');
            return;
        }
        try {
            showToast('info', 'Generating QR Code...');

            const response = await fetch(CERT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_qr_url',
                    id: cert.id,
                    shop_id: currentShop.shop_id
                })
            });

            let data;
            const rawText = await response.text();
            try {
                data = JSON.parse(rawText);
            } catch (parseErr) {
                console.error('❌ QR response was not valid JSON:', rawText);
                showToast('error', 'Server returned an invalid response', rawText.slice(0, 300));
                return;
            }

            if (data.status === 'success' && data.data?.short_url) {
                const qrDataUrl = await QRCode.toDataURL(data.data.short_url, {
                    errorCorrectionLevel: 'L',
                    width: 300,
                    margin: 0
                });

                const win = window.open('', '_blank', 'width=350,height=400');
                if (win) {
                    win.document.write(`
                        <html>
                        <head><title>QR Code</title></head>
                        <body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8fafc;font-family:Arial,sans-serif;">
                            <div style="text-align:center;padding:20px;background:white;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.1);">
                                <h3 style="margin-bottom:12px;color:#1f2937;">${cert.certificate_number}</h3>
                                <img src="${qrDataUrl}" style="width:250px;height:250px;margin:10px 0;" />
                                <p style="font-size:12px;color:#6b7280;word-break:break-all;">${data.data.short_url}</p>
                                <p style="font-size:11px;color:#9ca3af;margin-top:8px;">Scan to verify certificate</p>
                            </div>
                        </body>
                        </html>
                    `);
                    win.document.close();
                    showToast('success', 'QR Code generated!');
                } else {
                    showToast('error', 'Popup blocked. Please allow popups.');
                }
            } else {
                showToast('error', data.message || 'Failed to generate QR', JSON.stringify(data));
            }
        } catch (error) {
            console.error('❌ QR Error:', error);
            showToast('error', 'QR generation failed: ' + error.message);
        }
    };

    const printCertificate = (cert) => {
        const printUrl = `${CERT_API}?action=verify&certificate=${cert.certificate_number}&shop_id=${currentShop?.shop_id}&print=1`;
        window.open(printUrl, '_blank');
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try { return new Date(date).toLocaleDateString(); } catch { return date; }
    };

    const getModeBadge = (mode) => {
        const modes = {
            campus: { color: '#3b82f6', bg: '#dbeafe' },
            remote: { color: '#8b5cf6', bg: '#ede9fe' },
            hybrid: { color: '#f59e0b', bg: '#fef3c7' }
        };
        const m = modes[mode] || modes.campus;
        return (
            <span style={{ background: m.bg, color: m.color, padding: '2px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </span>
        );
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Toast Notification */}
            {toast.show && (
                <div style={{
                    position: 'fixed', bottom: '20px', right: '20px',
                    padding: '14px 24px', borderRadius: '12px',
                    background: toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : toast.type === 'info' ? '#3b82f6' : '#ef4444',
                    color: 'white', zIndex: 9999, maxWidth: '400px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    animation: 'slideIn 0.3s ease'
                }}>
                    <div style={{ fontWeight: '600' }}>{toast.message}</div>
                    {debugInfo && <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px', wordBreak: 'break-all' }}>{debugInfo}</div>}
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                        🎓 Internship Certificates
                    </h1>
                    <p style={{ color: '#6b7280', margin: '4px 0 0' }}>
                        {filtered.length} certificates issued
                        {currentShop && <span style={{ marginLeft: '12px', fontSize: '12px', background: '#f3f4f6', padding: '2px 12px', borderRadius: '12px' }}>Shop: {currentShop.shop_name}</span>}
                    </p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} /> New Certificate
                </button>
            </div>

            {/* Search */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                        type="text"
                        placeholder="Search by name, number, university..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 14px 10px 40px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    />
                </div>
                <button
                    onClick={fetchCertificates}
                    disabled={loading}
                    style={{
                        padding: '10px 16px',
                        background: '#f3f4f6',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '14px'
                    }}
                >
                    <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTop: '3px solid #2d5a87', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
                    <p style={{ color: '#6b7280' }}>Loading certificates...</p>
                </div>
            ) : !currentShop ? (
                <div style={{ textAlign: 'center', padding: '60px', background: '#f8fafc', borderRadius: '16px' }}>
                    <p style={{ color: '#6b7280', fontSize: '16px' }}>⚠️ Please select a shop to manage certificates</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto', background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>#</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>Certificate</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>Student</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>University</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>Role</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>Mode</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#4b5563' }}>Issued</th>
                                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#4b5563' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                        No certificates found. Click "New Certificate" to create one.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((cert, idx) => (
                                    <tr key={cert.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>{idx + 1}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <span style={{ fontWeight: '500', color: '#1f2937' }}>{cert.certificate_number}</span>
                                        </td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {cert.student_picture && (
                                                    <img src={cert.student_picture} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} />
                                                )}
                                                <span style={{ fontWeight: '500' }}>{cert.student_name}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#4b5563' }}>{cert.university_name}</td>
                                        <td style={{ padding: '12px 16px', color: '#4b5563' }}>{cert.role}</td>
                                        <td style={{ padding: '12px 16px' }}>{getModeBadge(cert.mode)}</td>
                                        <td style={{ padding: '12px 16px', color: '#6b7280' }}>{formatDate(cert.issuance_date)}</td>
                                        <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <button onClick={() => generateQRCode(cert)} style={{ background: '#dbeafe', color: '#1e40af', border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }} title="Generate QR">
                                                    <FontAwesomeIcon icon={faQrcode} />
                                                </button>
                                                <button onClick={() => printCertificate(cert)} style={{ background: '#fef3c7', color: '#92400e', border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }} title="Print/View">
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                                <button onClick={() => handleEdit(cert)} style={{ background: '#d1fae5', color: '#065f46', border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }} title="Edit">
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button onClick={() => handleDelete(cert.id)} style={{ background: '#fee2e2', color: '#991b1b', border: 'none', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }} title="Delete">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: '20px'
                }} onClick={() => setShowModal(false)}>
                    <div style={{
                        background: 'white',
                        borderRadius: '24px',
                        padding: '32px',
                        maxWidth: '800px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                                {editingId ? 'Edit Certificate' : 'New Internship Certificate'}
                            </h2>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <label style={labelStyle}>Student Name *</label>
                                    <input type="text" name="student_name" value={formData.student_name} onChange={handleInputChange} required style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Father's Name</label>
                                    <input type="text" name="father_name" value={formData.father_name} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>CNIC Number</label>
                                    <input type="text" name="cnic_number" value={formData.cnic_number} onChange={handleInputChange} placeholder="XXXXX-XXXXXXX-X" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>University *</label>
                                    <input type="text" name="university_name" value={formData.university_name} onChange={handleInputChange} required style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Department</label>
                                    <input type="text" name="department" value={formData.department} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Semester</label>
                                    <input type="text" name="semester" value={formData.semester} onChange={handleInputChange} placeholder="e.g. 6th Semester" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Role / Position *</label>
                                    <input type="text" name="role" value={formData.role} onChange={handleInputChange} required placeholder="e.g. Full Stack Developer" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Mode</label>
                                    <select name="mode" value={formData.mode} onChange={handleInputChange} style={inputStyle}>
                                        <option value="campus">Campus</option>
                                        <option value="remote">Remote</option>
                                        <option value="hybrid">Hybrid</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>From Date</label>
                                    <input type="date" name="internship_from" value={formData.internship_from} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>To Date</label>
                                    <input type="date" name="internship_to" value={formData.internship_to} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Issuance Date</label>
                                    <input type="date" name="issuance_date" value={formData.issuance_date} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Student Email</label>
                                    <input type="email" name="student_email" value={formData.student_email} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Student Phone</label>
                                    <input type="text" name="student_phone" value={formData.student_phone} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Supervisor Name</label>
                                    <input type="text" name="supervisor_name" value={formData.supervisor_name} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Supervisor Designation</label>
                                    <input type="text" name="supervisor_designation" value={formData.supervisor_designation} onChange={handleInputChange} style={inputStyle} />
                                </div>
                                
                                {/* ===== FILE UPLOAD: STUDENT PICTURE ===== */}
                                <div style={{ gridColumn: '1 / -1', border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '16px', background: '#fafafa' }}>
                                    <label style={{ ...labelStyle, marginBottom: '8px' }}>
                                        <FontAwesomeIcon icon={faUser} style={{ marginRight: '6px' }} /> Student Picture
                                    </label>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div>
                                            <input 
                                                type="file" 
                                                ref={studentPicRef}
                                                accept="image/*" 
                                                onChange={handleStudentPicChange}
                                                style={{ fontSize: '13px' }}
                                            />
                                            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>JPG, PNG (max 5MB)</p>
                                        </div>
                                        {(studentPicPreview || formData.student_picture) && (
                                            <div style={{ position: 'relative' }}>
                                                <img 
                                                    src={studentPicPreview || formData.student_picture} 
                                                    alt="Preview" 
                                                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb' }}
                                                />
                                                {(studentPicPreview || formData.student_picture) && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            setStudentPicPreview('');
                                                            setStudentPicFile(null);
                                                            setFormData(prev => ({ ...prev, student_picture: '' }));
                                                            if (studentPicRef.current) studentPicRef.current.value = '';
                                                        }}
                                                        style={{
                                                            position: 'absolute', top: '-6px', right: '-6px',
                                                            background: '#ef4444', color: 'white', border: 'none',
                                                            borderRadius: '50%', width: '20px', height: '20px',
                                                            fontSize: '10px', cursor: 'pointer', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center'
                                                        }}
                                                    >×</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* ===== FILE UPLOAD: CERTIFICATE IMAGE ===== */}
                                <div style={{ gridColumn: '1 / -1', border: '2px dashed #e5e7eb', borderRadius: '12px', padding: '16px', background: '#fafafa' }}>
                                    <label style={{ ...labelStyle, marginBottom: '8px' }}>
                                        <FontAwesomeIcon icon={faImage} style={{ marginRight: '6px' }} /> Certificate Image / Document
                                    </label>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <div>
                                            <input 
                                                type="file" 
                                                ref={certFileRef}
                                                accept="image/*" 
                                                onChange={handleCertFileChange}
                                                style={{ fontSize: '13px' }}
                                            />
                                            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>JPG, PNG (max 5MB)</p>
                                        </div>
                                        {(certFilePreview || formData.certificate_picture) && (
                                            <div style={{ position: 'relative' }}>
                                                <img 
                                                    src={certFilePreview || formData.certificate_picture} 
                                                    alt="Preview" 
                                                    style={{ width: '80px', height: '56px', borderRadius: '8px', objectFit: 'cover', border: '2px solid #e5e7eb' }}
                                                />
                                                {(certFilePreview || formData.certificate_picture) && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            setCertFilePreview('');
                                                            setCertFile(null);
                                                            setFormData(prev => ({ ...prev, certificate_picture: '' }));
                                                            if (certFileRef.current) certFileRef.current.value = '';
                                                        }}
                                                        style={{
                                                            position: 'absolute', top: '-6px', right: '-6px',
                                                            background: '#ef4444', color: 'white', border: 'none',
                                                            borderRadius: '50%', width: '20px', height: '20px',
                                                            fontSize: '10px', cursor: 'pointer', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center'
                                                        }}
                                                    >×</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 24px', background: '#f3f4f6', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={submitting} style={{
                                    padding: '10px 32px',
                                    background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={uploading ? faUpload : faSave} />}
                                    {uploading ? 'Uploading...' : editingId ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border 0.2s',
    background: '#fafafa'
};

const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#4b5563',
    marginBottom: '4px'
};

export default InternshipForm;